import React, { useState, useEffect } from 'react';
import {
  Heart,
  MoreHorizontal,
  Share,
  Plus,
  ArrowLeft,
  Music,
  Languages
} from 'lucide-react';
import dataManager from '../utils/dataManager';

const PlayerPage = ({ song, songInfo: propSongInfo, onClose, currentPage }) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [songInfo, setSongInfo] = useState(propSongInfo);
  const [loading, setLoading] = useState(!propSongInfo);
  const [lyricsData, setLyricsData] = useState([]);
  const [translatedLyricsData, setTranslatedLyricsData] = useState([]);
  const [showTranslation, setShowTranslation] = useState(false);
  const [error, setError] = useState(null);
  const [currentLyricIndex, setCurrentLyricIndex] = useState(-1);
  const [settings, setSettings] = useState({ lyricsFontSize: 'medium' });

  // 加载设置 (包括页面变化时重新加载)
  useEffect(() => {
    const loadedSettings = dataManager.getSettings();
    setSettings(loadedSettings);
  }, [currentPage]);

  // 监听播放栏的音频时间更新
  useEffect(() => {
    const handleTimeUpdate = () => {
      const musicPlayer = document.querySelector('audio');
      if (musicPlayer) {
        setCurrentTime(musicPlayer.currentTime);
      }
    };

    const musicPlayer = document.querySelector('audio');
    if (musicPlayer) {
      musicPlayer.addEventListener('timeupdate', handleTimeUpdate);
      return () => {
        musicPlayer.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, []);

  // 根据当前时间更新高亮歌词（优化性能，避免频繁更新）
  useEffect(() => {
    const currentLyrics = showTranslation ? translatedLyricsData : lyricsData;
    if (currentLyrics.length === 0) return;

    // 找到当前时间应该显示的歌词索引
    let activeIndex = -1;
    for (let i = 0; i < currentLyrics.length; i++) {
      if (currentTime >= currentLyrics[i].time) {
        activeIndex = i;
      } else {
        break;
      }
    }
    
    // 只有当索引真正改变时才更新
    if (activeIndex !== currentLyricIndex) {
      setCurrentLyricIndex(activeIndex);
    }
  }, [currentTime, lyricsData, translatedLyricsData, showTranslation, currentLyricIndex]);

  // 获取歌曲详细信息
  const fetchSongInfo = async () => {
    if (!song || propSongInfo) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `https://music-dl.sayqz.com/api/?source=${song.platform}&type=info&id=${song.id}`
      );
      const data = await response.json();
      
      if (data.code === 200) {
        setSongInfo(data.data);
      } else {
        setError('获取歌曲信息失败');
      }
    } catch (error) {
      console.error('API调用失败:', error);
      setError('网络连接失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取歌词
  const fetchLyrics = async () => {
    if (!songInfo?.lrc) {
      setLyricsData([{ time: 0, text: '暂无歌词' }]);
      return;
    }

    try {
      const lrcResponse = await fetch(songInfo.lrc);
      const lrcText = await lrcResponse.text();
      
      // 解析歌词和翻译
      const { originalLyrics, translation } = parseLrcContent(lrcText);
      setLyricsData(originalLyrics);
      setTranslatedLyricsData(translation);
    } catch (error) {
      console.error('获取歌词失败:', error);
      setLyricsData([{ time: 0, text: '暂无歌词' }]);
    }
  };

  useEffect(() => {
    if (!propSongInfo) {
      fetchSongInfo();
    }
  }, [song?.id, song?.platform, propSongInfo]);

  useEffect(() => {
    if (songInfo?.lrc) {
      fetchLyrics();
    }
  }, [songInfo?.lrc]);

  // 解析时间字符串为秒数
  const parseTime = (timeStr) => {
    // 标准LRC格式：[mm:ss.xx] 或 [mm:ss.xxx]
    const standardMatch = timeStr.match(/\[(\d{1,2}):(\d{2})\.?(\d{1,3})?\]/);
    if (standardMatch) {
      const minutes = parseInt(standardMatch[1], 10);
      const seconds = parseInt(standardMatch[2], 10);
      const milliseconds = parseInt(standardMatch[3] || '0', 10);
      
      // 处理不同长度的毫秒数
      const ms = standardMatch[3] ? 
        (standardMatch[3].length === 3 ? milliseconds / 1000 : milliseconds / 100) : 0;
      
      return minutes * 60 + seconds + ms;
    }
    
    // 网易云格式：[毫秒数,持续时间]
    const neteaseMatch = timeStr.match(/\[(\d+),\d+\]/);
    if (neteaseMatch) {
      return parseInt(neteaseMatch[1], 10) / 1000;
    }
    
    return 0;
  };

  // 解析LRC歌词内容
  const parseLrcContent = (lrcText) => {
    if (!lrcText || lrcText === '暂无歌词') {
      return { originalLyrics: [{ time: 0, text: '暂无歌词' }], translation: [] };
    }

    const lines = lrcText.split('\n');
    const originalLines = [];
    const translationLines = [];
    let isTranslationSection = false;

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;

      // 检查是否是翻译部分的标识
      if (trimmedLine.includes('[翻译]') || trimmedLine.includes('QQ音乐享有本翻译作品的著作权')) {
        isTranslationSection = true;
        continue;
      }

      // 跳过歌曲信息行（如：[ti:], [ar:], [al:] 等）
      if (trimmedLine.match(/^\[(ti|ar|al|by|offset|ver):/)) {
        continue;
      }

      // 处理网易云逐字歌词格式
      if (trimmedLine.includes('(') && trimmedLine.includes(')')) {
        const neteaseMatch = trimmedLine.match(/\[(\d+),\d+\](.+)/);
        if (neteaseMatch) {
          const time = parseInt(neteaseMatch[1], 10) / 1000;
          // 提取逐字歌词中的文字内容
          const content = neteaseMatch[2];
          const textMatch = content.match(/\)([^(]+)/g);
          const text = textMatch ? textMatch.map(m => m.substring(1)).join('') : content;
          
          if (text && !text.startsWith('//')) {
            const lyricItem = { time, text: text.trim() };
            if (isTranslationSection) {
              translationLines.push(lyricItem);
            } else {
              originalLines.push(lyricItem);
            }
          }
          continue;
        }
      }

      // 标准LRC格式处理
      const timeMatch = trimmedLine.match(/\[(\d{1,2}):(\d{2})\.?(\d{1,3})?\]/);
      if (timeMatch) {
        const time = parseTime(trimmedLine);
        let text = trimmedLine.replace(/\[\d{1,2}:\d{2}\.?\d*\]/g, '').trim();
        
        // 跳过空行、特殊标记和歌曲信息
        if (!text || text === '//' || text.startsWith('//') || 
            text.match(/^(词|曲|编曲|制作人|合声|录音师|混音师|母带)[:：]/)) {
          continue;
        }

        const lyricItem = { time, text };

        if (isTranslationSection) {
          translationLines.push(lyricItem);
        } else {
          originalLines.push(lyricItem);
        }
      }
    }

    // 按时间排序
    originalLines.sort((a, b) => a.time - b.time);
    translationLines.sort((a, b) => a.time - b.time);

    // 如果没有解析到任何歌词，返回默认
    if (originalLines.length === 0) {
      originalLines.push({ time: 0, text: '暂无歌词' });
    }

    return {
      originalLyrics: originalLines,
      translation: translationLines
    };
  };

  // 获取歌词字体大小样式
  const getLyricsSizeClasses = (index) => {
    const { lyricsFontSize } = settings;
    const isActive = index === currentLyricIndex;
    const isAdjacent = index === currentLyricIndex - 1 || index === currentLyricIndex + 1;

    // 基础大小配置
    const sizeConfig = {
      small: {
        active: 'text-lg font-semibold',
        adjacent: 'text-base',
        normal: 'text-sm'
      },
      medium: {
        active: 'text-2xl font-semibold',
        adjacent: 'text-lg',
        normal: 'text-base'
      },
      large: {
        active: 'text-3xl font-semibold',
        adjacent: 'text-xl',
        normal: 'text-lg'
      },
      xlarge: {
        active: 'text-4xl font-semibold',
        adjacent: 'text-2xl',
        normal: 'text-xl'
      }
    };

    const config = sizeConfig[lyricsFontSize] || sizeConfig.medium;
    
    if (isActive) {
      return `text-primary-600 ${config.active}`;
    } else if (isAdjacent) {
      return `text-dark-600 ${config.adjacent}`;
    } else {
      return `text-dark-500 ${config.normal}`;
    }
  };

  // 渲染歌词 - Apple Music 风格
  const renderLyrics = (lyricsArray, isTranslation = false) => {
    if (!lyricsArray || lyricsArray.length === 0) {
      return (
        <div className="text-center text-dark-500 py-16">
          <Music size={48} className="mx-auto mb-4 opacity-30" />
          <p className="text-lg">
            {isTranslation ? '暂无翻译' : '暂无歌词'}
          </p>
        </div>
      );
    }

    return lyricsArray.map((lyric, index) => (
      <div
        key={index}
        id={`lyric-${index}`}
        className={`py-4 transition-all duration-500 ease-out text-center leading-relaxed ${getLyricsSizeClasses(index)}`}
        style={{
          opacity: index === currentLyricIndex ? 1 : 
                   index === currentLyricIndex - 1 || index === currentLyricIndex + 1 ? 0.8 : 0.4
        }}
      >
        {lyric.text}
      </div>
    ));
  };

  // 自动滚动到当前歌词（防抖处理）- Apple Music 风格
  useEffect(() => {
    if (currentLyricIndex >= 0) {
      const timer = setTimeout(() => {
        const element = document.getElementById(`lyric-${currentLyricIndex}`);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 200); // 增加防抖时间，让滚动更平滑

      return () => clearTimeout(timer);
    }
  }, [currentLyricIndex]);

  // 获取平台信息
  const getPlatformInfo = (platform) => {
    const platformMap = {
      qq: { name: 'QQ音乐', icon: '/icon/qq.png' },
      netease: { name: '网易云音乐', icon: '/icon/netease.png' },
      kuwo: { name: '酷我音乐', icon: '/icon/kw.png' }
    };
    return platformMap[platform] || { name: platform.toUpperCase(), icon: null };
  };

  if (!song) {
    return (
      <div className="flex-1 bg-dark-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-dark-600 mb-2">暂无播放内容</div>
          <p className="text-dark-500">请选择一首歌曲开始播放</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex-1 bg-dark-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <div className="text-lg text-dark-600">加载中...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 bg-dark-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-red-600 mb-2">加载失败</div>
          <p className="text-dark-500 mb-4">{error}</p>
          <button
            onClick={fetchSongInfo}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors duration-200"
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  const platformInfo = getPlatformInfo(song?.platform || '');

  return (
    <div className="flex-1 bg-dark-50 overflow-hidden">
      {/* Apple Music 风格双栏布局 */}
      <div className="flex h-full">
        {/* 左栏：歌曲信息 */}
        <div className="w-1/2 flex flex-col items-center justify-center p-12 bg-gradient-to-br from-dark-50 to-dark-100">
          {/* 专辑封面 */}
          <div className="w-72 h-72 bg-dark-200 rounded-3xl shadow-2xl mb-8 overflow-hidden">
            {songInfo?.pic ? (
              <img
                src={songInfo.pic}
                alt={songInfo?.name || song?.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : (
              <div className="w-full h-full bg-dark-200 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-dark-300 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Music size={32} className="text-dark-500" />
                  </div>
                  <p className="text-dark-500">暂无封面</p>
                </div>
              </div>
            )}
          </div>

          {/* 歌曲信息 */}
          <div className="text-center mb-6 w-full max-w-sm">
            {/* 歌名和来源 */}
            <div className="flex items-center justify-center gap-4 mb-3">
              <h2 className="text-3xl font-bold text-dark-800 truncate">
                {songInfo?.name || song?.name}
              </h2>
              {platformInfo.icon && (
                <div className="w-10 h-10 p-2 rounded-xl overflow-hidden flex-shrink-0">
                  <img
                    src={platformInfo.icon}
                    alt={platformInfo.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
            </div>
            
            <p className="text-xl text-dark-600 mb-4 truncate">
              {songInfo?.artist || song?.artist}
            </p>
            
            {/* 专辑信息 */}
            <div className="text-dark-500">
              <span className="truncate">
                {songInfo?.album || song?.album || '未知专辑'}
              </span>
            </div>
          </div>


        </div>

        {/* 右栏：歌词 */}
        <div className="w-1/2 bg-dark-50 flex flex-col relative overflow-hidden">
          {/* 翻译切换按钮 - 浮动在右上角 */}
          {translatedLyricsData.length > 0 && (
            <button
              onClick={() => setShowTranslation(!showTranslation)}
              className="absolute top-6 right-6 z-10 flex items-center gap-2 px-4 py-2 bg-dark-100 backdrop-blur-sm text-dark-700 rounded-full hover:bg-dark-200 transition-colors duration-200"
            >
              <Languages size={16} />
              <span className="text-sm">
                {showTranslation ? '原文' : '翻译'}
              </span>
            </button>
          )}
          
          {/* 歌词内容 - Apple Music 风格滚动 */}
          <div className="flex-1 overflow-y-auto px-8 py-16 scrollbar-thin">
            <div className="max-w-2xl mx-auto">
              {renderLyrics(showTranslation ? translatedLyricsData : lyricsData, showTranslation)}
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default PlayerPage;
